/**
 * Firestore 永続キャッシュの破損からの回復ヘルパー（純関数、firebase.ts から使用）。
 *
 * #7341 postmortem フォローアップ（docs/98 R-66 / docs/111 Wave 2）の ARCB-6 / MT-4 を、
 * firebase.ts の副作用（initializeApp 等）から切り離して単体テスト可能にするために分離。
 */

/** Firestore デフォルトアプリの persistenceKey（FirebaseApp 名）。 */
export const DEFAULT_APP_NAME = '[DEFAULT]';

/**
 * キャッシュバスター実行結果から「版数 (CACHE_VERSION_KEY) を前進させてよいか」を判定する純関数。
 *
 * - `true`（全 IndexedDB 削除が成功）のときだけ版数を前進させる（ARCB-1 不変条件）。
 * - `false`（一部が onblocked / error で未削除）→ 前進させない（次回起動で再削除）。
 * - `'timeout'`（env-2: 削除が時間内に終わらず Promise.race のタイムアウトが勝った）→ 前進させない。
 *   ハングしてもブートは進める一方、治療済み誤認は避け、次回起動で再試行する。
 */
export function shouldAdvanceCacheVersion(outcome: boolean | 'timeout'): boolean {
  return outcome === true;
}

/**
 * Firestore が永続キャッシュ (IndexedDB) に使う DB 名を返す（フォールバック削除用）。
 *
 * `indexedDB.databases()` 非対応ブラウザ（Safari < 16 / 旧 iOS）では `firestore/*` を列挙できないため、
 * キャッシュバスターはこの「実名」を直接 `deleteDatabase` する。名前が 1 文字でも違うと no-op になり、
 * 壊れた状態のユーザーを治療できないまま版数だけ前進してしまう（**ARCB-6**、docs/111 Wave 2）。
 *
 * 命名規則（SDK `@firebase/firestore@4.14.1` `dist/common-7a7519be.esm.js` で実検証）:
 *   - storagePrefix = `"firestore/" + persistenceKey + "/" + dbId + "/"`（L12858）
 *       - persistenceKey = FirebaseApp 名。デフォルトアプリは `[DEFAULT]`
 *       - dbId = projectId。**デフォルト DB (`(default)`) は projectId のみ**で、`.{database}` 接尾辞を
 *         付けない（SDK: `e.isDefaultDatabase || (n += "." + e.database)`）
 *   - dbName = storagePrefix + `"main"`（L12482, `Ot = "main"`）
 *   → `firestore/[DEFAULT]/<projectId>/main`
 *
 * 旧実装は `firestore/<projectId>/(default)/main` を消しており（persistenceKey の位置が違い、
 * default DB に `(default)` を付けていた）、実在しない名前のため完全な no-op だった。
 */
export function firestoreIdbNames(projectId: string, appName: string = DEFAULT_APP_NAME): string[] {
  if (!projectId) return [];
  return [`firestore/${appName}/${projectId}/main`];
}

/**
 * Firestore クライアントが自爆し、永続キャッシュを作り直さないと復帰しないエラーかを判定する。
 * firebase.ts の `unhandledrejection` / `error` ハンドラで使い、検知したらキャッシュバスター版数を
 * 消して 1 回だけ reload する。
 *
 * 検知対象:
 *   - **#7341 内部アサーション**: `INTERNAL ASSERTION FAILED` / (`FIRESTORE` ∧ `Unexpected state`)
 *   - **MT-4 複数タブ terminate**: `The client has already been terminated.`
 *     （SDK L15277/19758/21287）。再デプロイ直後に別タブの deleteDatabase が当該タブの Firestore を
 *     terminate した後、以後の read/write がこれを投げて固着する。自動 reload 対象に含める。
 *   - **タブ状態不整合**: `The current tab is not in the required state ...`（SDK L1631）
 */
export function isRecoverableFirestoreError(reason: unknown): boolean {
  let msg = '';
  if (typeof reason === 'string') {
    msg = reason;
  } else if (reason && typeof reason === 'object') {
    const m = (reason as { message?: unknown }).message;
    msg = typeof m === 'string' ? m : String(reason);
  }
  if (!msg) return false;
  return (
    msg.includes('INTERNAL ASSERTION FAILED') ||
    (msg.includes('FIRESTORE') && msg.includes('Unexpected state')) ||
    msg.includes('already been terminated') ||
    msg.includes('not in the required state')
  );
}
